import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Package, Mail, ShoppingBag, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const EMPTY_FORM = { name: "", description: "", price: "", category: "candles", image_url: "", badge: "", stock: 10, active: true };
const CATEGORIES = ["candles", "lip-balms", "soaps", "lotions"];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.me()
      .then((u) => { setUser(u); loadAll(); })
      .catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [p, w, o, m] = await Promise.all([api.adminProducts(), api.adminWaitlist(), api.adminOrders(), api.adminMessages()]);
      setProducts(p); setWaitlist(w); setOrders(o); setMessages(m);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    navigate("/admin/login");
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, description: p.description, price: String(p.price), category: p.category, image_url: p.image_url, badge: p.badge || "", stock: p.stock, active: p.active });
    setShowForm(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) || 0 };
    try {
      if (editingId) {
        await api.updateProduct(editingId, payload);
        toast.success("Product updated");
      } else {
        await api.createProduct(payload);
        toast.success("Product added to the shop");
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY_FORM);
      const p = await api.adminProducts();
      setProducts(p);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the shop?`)) return;
    try {
      await api.deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Product removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const stats = useMemo(() => ([
    { label: "Products", value: products.length, icon: Package },
    { label: "Honey Waitlist", value: waitlist.length, icon: Users },
    { label: "Orders", value: orders.length, icon: ShoppingBag },
    { label: "Messages", value: messages.length, icon: Mail },
  ]), [products, waitlist, orders, messages]);

  if (!user) return <div className="min-h-screen bg-[#FAF5EE] flex items-center justify-center text-[#8C7A6B]" data-testid="admin-loading">Loading...</div>;

  const TABS = [
    { id: "products", label: "Products" },
    { id: "waitlist", label: "Honey Waitlist" },
    { id: "orders", label: "Orders" },
    { id: "messages", label: "Messages" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF5EE]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E6D7C3] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
            <Logo size={32} />
            <span className="font-serif-display font-bold text-[#2C221E]">McGee's Bees · Shop Manager</span>
          </Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-[#5A4D44] hover:text-[#9A3412] transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-[#E6D7C3] p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F3EBE0] flex items-center justify-center">
                <s.icon className="w-5 h-5 text-[#B45309]" />
              </div>
              <div>
                <p className="font-serif-display text-2xl font-bold text-[#2C221E]">{s.value}</p>
                <p className="text-xs text-[#8C7A6B]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2.5 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#2C221E] text-[#FAF5EE]" : "bg-white border border-[#E6D7C3] text-[#5A4D44] hover:text-[#B45309]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div data-testid="admin-products-panel">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif-display text-xl font-bold text-[#2C221E]">Shop Products</h2>
              <button
                onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setShowForm((v) => !v); }}
                data-testid="admin-add-product-button"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>

            {showForm && (
              <form onSubmit={saveProduct} className="bg-white rounded-2xl border border-[#E6D7C3] p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="admin-product-form">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" data-testid="product-form-name" className="px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]" />
                <input required type="number" step="0.01" min="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (USD)" data-testid="product-form-price" className="px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" className="px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D97706]">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("-", " ")}</option>)}
                </select>
                <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock count" data-testid="product-form-stock" className="px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]" />
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL (paste a product photo link)" data-testid="product-form-image" className="sm:col-span-2 px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]" />
                <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Badge (optional, e.g. Small Batch)" data-testid="product-form-badge" className="px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]" />
                <label className="flex items-center gap-2 text-sm text-[#5A4D44]">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="product-form-active" className="w-4 h-4 accent-[#D97706]" />
                  Visible in shop
                </label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" data-testid="product-form-description" className="sm:col-span-2 px-4 py-3 rounded-xl border border-[#E6D7C3] text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706] resize-none" />
                <div className="sm:col-span-2 flex gap-3">
                  <button type="submit" disabled={saving} data-testid="product-form-save" className="px-6 py-3 rounded-full bg-[#2C221E] hover:bg-[#D97706] text-[#FAF5EE] text-sm font-semibold transition-colors disabled:opacity-60">
                    {saving ? "Saving..." : editingId ? "Save Changes" : "Add Product"}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="product-form-cancel" className="px-6 py-3 rounded-full border border-[#E6D7C3] text-sm text-[#5A4D44] hover:bg-[#F3EBE0]">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="grid gap-4" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#E6D7C3] p-4 flex items-center gap-4" data-testid={`admin-product-row-${p.id}`}>
                  <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-xl object-cover bg-[#F3EBE0] border border-[#E6D7C3]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-display font-semibold text-[#2C221E] truncate">{p.name}</p>
                    <p className="text-xs text-[#8C7A6B]">{p.category} · ${p.price.toFixed(2)} · {p.stock} in stock {!p.active && "· hidden"}</p>
                  </div>
                  <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2.5 rounded-full hover:bg-[#F3EBE0] text-[#5A4D44]" aria-label={`Edit ${p.name}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => removeProduct(p.id, p.name)} data-testid={`admin-delete-${p.id}`} className="p-2.5 rounded-full hover:bg-[#9A3412]/10 text-[#9A3412]" aria-label={`Delete ${p.name}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "waitlist" && (
          <div className="bg-white rounded-2xl border border-[#E6D7C3] overflow-hidden" data-testid="admin-waitlist-panel">
            <div className="px-6 py-5 border-b border-[#E6D7C3]">
              <h2 className="font-serif-display text-xl font-bold text-[#2C221E]">Spring 2027 Honey Waitlist</h2>
            </div>
            {waitlist.length === 0 ? (
              <p className="px-6 py-10 text-sm text-[#8C7A6B]" data-testid="waitlist-empty">No signups yet — share your site to start the list.</p>
            ) : (
              <ul>
                {waitlist.map((w) => (
                  <li key={w.id} className="px-6 py-4 border-b border-[#F3EBE0] flex justify-between items-center text-sm" data-testid={`waitlist-row-${w.id}`}>
                    <div>
                      <p className="font-medium text-[#2C221E]">{w.name || "—"}</p>
                      <p className="text-[#8C7A6B]">{w.email}</p>
                    </div>
                    <span className="text-xs text-[#8C7A6B]">{new Date(w.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-white rounded-2xl border border-[#E6D7C3] overflow-hidden" data-testid="admin-orders-panel">
            <div className="px-6 py-5 border-b border-[#E6D7C3]">
              <h2 className="font-serif-display text-xl font-bold text-[#2C221E]">Orders</h2>
            </div>
            {orders.length === 0 ? (
              <p className="px-6 py-10 text-sm text-[#8C7A6B]" data-testid="orders-empty">No orders yet.</p>
            ) : (
              <ul>
                {orders.map((o) => (
                  <li key={o.id} className="px-6 py-4 border-b border-[#F3EBE0]" data-testid={`order-row-${o.id}`}>
                    <div className="flex justify-between items-center">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${o.status === "paid" ? "bg-[#2D4A3E]/10 text-[#2D4A3E]" : "bg-[#F3EBE0] text-[#8C7A6B]"}`} data-testid={`order-status-${o.id}`}>
                        {o.status}
                      </span>
                      <span className="font-serif-display font-bold text-[#2C221E]">${o.total?.toFixed(2)}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#5A4D44]">{o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                    <p className="mt-1 text-xs text-[#8C7A6B]">{new Date(o.created_at).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="bg-white rounded-2xl border border-[#E6D7C3] overflow-hidden" data-testid="admin-messages-panel">
            <div className="px-6 py-5 border-b border-[#E6D7C3]">
              <h2 className="font-serif-display text-xl font-bold text-[#2C221E]">Contact Messages</h2>
            </div>
            {messages.length === 0 ? (
              <p className="px-6 py-10 text-sm text-[#8C7A6B]" data-testid="messages-empty">No messages yet.</p>
            ) : (
              <ul>
                {messages.map((m) => (
                  <li key={m.id} className="px-6 py-4 border-b border-[#F3EBE0]" data-testid={`message-row-${m.id}`}>
                    <div className="flex justify-between items-baseline">
                      <p className="font-medium text-[#2C221E] text-sm">{m.name} <span className="text-[#8C7A6B] font-normal">· {m.email}</span></p>
                      <span className="text-xs text-[#8C7A6B]">{new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-[#5A4D44]">{m.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
